import React, { useState, useMemo } from 'react'
import {
  Box,
  SimpleGrid,
  Text,
  Flex,
  Badge,
  Button,
  Icon,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  useDisclosure,
  useToast,
  Spinner,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  HStack,
  VStack,
  Divider,
  Switch,
} from '@chakra-ui/react'
import ReactApexChart from 'react-apexcharts'
import {
  MdAttachMoney,
  MdConfirmationNumber,
  MdEvent,
  MdReceiptLong,
  MdTrendingUp,
  MdOutlineRemoveShoppingCart,
  MdRefresh,
  MdSearch,
  MdPictureAsPdf,
} from 'react-icons/md'

import Card from 'components/card/Card'
import IconBox from 'components/icons/IconBox'
import MiniStatistics from 'components/card/MiniStatistics'
import {
  useGetReportsAnalytics,
  useGetEventReport,
  useRefundOrder,
} from 'queries/report.query'
import { downloadFile } from 'utils/http'

export default function ReportsAnalyticsView() {
  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'whiteAlpha.700')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100')
  const tableTheadBg = useColorModeValue('white', 'navy.800')
  const selectBg = useColorModeValue('white', 'navy.900')
  const tableRowAltBg = useColorModeValue('gray.50', 'whiteAlpha.50')

  // Filters State
  const [selectedEventId, setSelectedEventId] = useState('all')
  const [startDateFilter, setStartDateFilter] = useState('')
  const [endDateFilter, setEndDateFilter] = useState('')
  const [gatewayFilter, setGatewayFilter] = useState('all')
  const [orderStatusFilter, setOrderStatusFilter] = useState('all')
  const [orderSearchQuery, setOrderSearchQuery] = useState('')
  const [includeBookingFee, setIncludeBookingFee] = useState(true)
  const [isExportingPdf, setIsExportingPdf] = useState(false)

  // API Queries
  const queryParams = useMemo(() => {
    const p = {}
    if (selectedEventId !== 'all') p.event_id = selectedEventId
    if (startDateFilter) p.start_date = startDateFilter
    if (endDateFilter) p.end_date = endDateFilter
    if (gatewayFilter !== 'all') p.gateway = gatewayFilter
    if (orderStatusFilter !== 'all') p.status = orderStatusFilter
    p.include_booking_fee = includeBookingFee ? 1 : 0
    return p
  }, [selectedEventId, startDateFilter, endDateFilter, gatewayFilter, orderStatusFilter, includeBookingFee])

  const eventReportParams = useMemo(() => ({
    include_booking_fee: includeBookingFee ? 1 : 0,
  }), [includeBookingFee])

  const { data, isLoading, refetch, isRefetching } = useGetReportsAnalytics(queryParams)
  const { data: eventReportResponse } = useGetEventReport(selectedEventId, eventReportParams)
  const eventReportData = eventReportResponse?.data || null

  const refundMutation = useRefundOrder()
  const toast = useToast()

  const [selectedOrder, setSelectedOrder] = useState(null)
  const [refundReason, setRefundReason] = useState('')
  const { isOpen: isRefundOpen, onOpen: onOpenRefund, onClose: onCloseRefund } = useDisclosure()

  const handleOpenRefund = (order) => {
    setSelectedOrder(order)
    setRefundReason('Customer cancellation request')
    onOpenRefund()
  }

  const handleConfirmRefund = async () => {
    if (!selectedOrder) return
    try {
      await refundMutation.mutateAsync({
        orderId: selectedOrder.id,
        reason: refundReason,
      })
      toast({
        title: 'Order Refunded',
        description: `Order ${selectedOrder.order_number} has been refunded and seats released.`,
        status: 'success',
        duration: 4000,
      })
      onCloseRefund()
    } catch (err) {
      toast({
        title: 'Refund failed',
        description: err?.response?.data?.message || 'Unable to process refund.',
        status: 'error',
        duration: 4000,
      })
    }
  }

  const handleExportPdf = async () => {
    if (selectedEventId === 'all') return
    try {
      setIsExportingPdf(true)
      const suffix = includeBookingFee ? '' : '-NetOnly'
      const filename = `Event-Financial-Report-${selectedEventId}${suffix}-${new Date().toISOString().slice(0, 10)}.pdf`
      await downloadFile(`reports/events/${selectedEventId}/pdf?include_booking_fee=${includeBookingFee ? 1 : 0}`, filename)
      toast({
        title: 'PDF Export Successful',
        description: includeBookingFee 
          ? 'Official event financial report downloaded (with booking fees).'
          : 'Official event net financial report downloaded (booking fees excluded).',
        status: 'success',
        duration: 4000,
      })
    } catch (err) {
      toast({
        title: 'Export Failed',
        description: err?.message || 'Failed to download event PDF report.',
        status: 'error',
        duration: 4000,
      })
    } finally {
      setIsExportingPdf(false)
    }
  }

  const handleResetFilters = () => {
    setSelectedEventId('all')
    setStartDateFilter('')
    setEndDateFilter('')
    setGatewayFilter('all')
    setOrderStatusFilter('all')
    setOrderSearchQuery('')
    setIncludeBookingFee(true)
  }

  const summary = data?.summary || {}
  const charts = data?.charts || {}
  const recentOrders = useMemo(() => data?.recent_orders || [], [data?.recent_orders])
  const eventsList = data?.events_list || []

  const filteredOrders = useMemo(() => {
    return recentOrders.filter((ord) => {
      // Strictly enforce event filtering so no orders from any other event can ever appear
      if (selectedEventId !== 'all' && String(ord.event_id) !== String(selectedEventId)) {
        return false
      }
      if (orderSearchQuery.trim() !== '') {
        const q = orderSearchQuery.toLowerCase()
        const matchesCode = (ord.order_number || '').toLowerCase().includes(q)
        const matchesName = (ord.customer_name || '').toLowerCase().includes(q)
        const matchesEmail = (ord.customer_email || '').toLowerCase().includes(q)
        const matchesEvent = (ord.event_title || '').toLowerCase().includes(q)
        if (!matchesCode && !matchesName && !matchesEmail && !matchesEvent) {
          return false
        }
      }
      return true
    })
  }, [recentOrders, orderSearchQuery, selectedEventId])

  const totalTicketsInFilteredOrders = useMemo(() => {
    return filteredOrders.reduce((sum, ord) => sum + (ord.tickets_count || 1), 0)
  }, [filteredOrders])

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="500px">
        <Spinner size="xl" color="brand.500" thickness="4px" />
      </Flex>
    )
  }

  // 1. Sales Trend Area Chart Options
  const salesTrendOptions = {
    chart: {
      type: 'area',
      toolbar: { show: false },
      fontFamily: 'inherit',
    },
    colors: ['#4318FF', '#01B574'],
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2.5 },
    xaxis: {
      categories: charts.sales_trend?.labels || [],
      labels: { style: { colors: '#A3AED0', fontSize: '12px' } },
    },
    yaxis: [
      {
        title: { text: 'Revenue (€)', style: { color: '#4318FF' } },
        labels: {
          style: { colors: '#A3AED0' },
          formatter: (val) => `€${val}`,
        },
      },
      {
        opposite: true,
        title: { text: 'Orders', style: { color: '#01B574' } },
        labels: {
          style: { colors: '#A3AED0' },
          formatter: (val) => Math.round(val),
        },
      },
    ],
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (val, { seriesIndex }) =>
          seriesIndex === 0 ? `€${val.toFixed(2)}` : `${val} orders`,
      },
    },
    grid: { borderColor: 'rgba(163, 174, 208, 0.2)' },
  }

  // 2. Status Breakdown Donut Chart Options
  const statusDonutOptions = {
    chart: { type: 'donut', fontFamily: 'inherit' },
    labels: charts.status_breakdown?.labels || ['Paid', 'Pending', 'Refunded', 'Cancelled'],
    colors: ['#01B574', '#FFB547', '#EE5D50', '#8F9BBA'],
    legend: { position: 'bottom', labels: { colors: textColorSecondary } },
    dataLabels: { enabled: true },
    tooltip: { theme: 'dark' },
  }

  // 3. Revenue by Event Bar Chart Options
  const eventBarOptions = {
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
    colors: ['#7551FF'],
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '45%',
        distributed: false,
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: charts.revenue_by_event?.labels || [],
      labels: { style: { colors: '#A3AED0', fontSize: '11px' } },
    },
    yaxis: {
      labels: {
        style: { colors: '#A3AED0' },
        formatter: (val) => `€${val}`,
      },
    },
    tooltip: {
      theme: 'dark',
      y: { formatter: (val) => `€${val.toFixed(2)}` },
    },
    grid: { borderColor: 'rgba(163, 174, 208, 0.2)' },
  }

  const formatEur = (val) => {
    const n = Number(val || 0)
    if (Math.abs(n) < 0.0001) return '€0.00'
    if (n < 0) return `-€${Math.abs(n).toFixed(2)}`
    return `€${n.toFixed(2)}`
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {/* Page Header */}
      <Flex justifyContent="space-between" alignItems="center" mb="20px" flexWrap="wrap" gap={3}>
        <Box>
          <Text color={textColor} fontSize="26px" fontWeight="700" lineHeight="120%">
            Financial Reports & Analytics
          </Text>
          <Text color={textColorSecondary} fontSize="sm" mt={1}>
            Comprehensive event sales, booking fees, refunds, and financial accounting
          </Text>
        </Box>
        <HStack spacing={3}>
          {selectedEventId !== 'all' && (
            <Button
              leftIcon={<MdPictureAsPdf />}
              colorScheme="purple"
              variant="solid"
              size="sm"
              borderRadius="12px"
              isLoading={isExportingPdf}
              onClick={handleExportPdf}
            >
              Export PDF Report
            </Button>
          )}
          <Button
            leftIcon={<MdRefresh />}
            colorScheme="brand"
            variant="outline"
            size="sm"
            borderRadius="12px"
            isLoading={isRefetching}
            onClick={() => refetch()}
          >
            Refresh Data
          </Button>
        </HStack>
      </Flex>

      {/* Advanced Filter Toolbar */}
      <Card p="18px" mb="25px" borderRadius="16px">
        <Flex gap={3} wrap="wrap" align="center" justify="space-between">
          <HStack spacing={3} wrap="wrap" flex="1">
            {/* Event Selector */}
            <Box minW="220px">
              <Text fontSize="11px" fontWeight="bold" color="gray.500" mb={1} textTransform="uppercase">
                Select Event
              </Text>
              <Select
                size="sm"
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                borderRadius="md"
                bg={selectBg}
              >
                <option value="all">All Events (Platform Overview)</option>
                {eventsList.map((evt) => (
                  <option key={evt.id} value={evt.id}>
                    {evt.title} ({evt.status})
                  </option>
                ))}
              </Select>
            </Box>

            {/* Date From */}
            <Box minW="140px">
              <Text fontSize="11px" fontWeight="bold" color="gray.500" mb={1} textTransform="uppercase">
                Date From
              </Text>
              <Input
                size="sm"
                type="date"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
                borderRadius="md"
              />
            </Box>

            {/* Date To */}
            <Box minW="140px">
              <Text fontSize="11px" fontWeight="bold" color="gray.500" mb={1} textTransform="uppercase">
                Date To
              </Text>
              <Input
                size="sm"
                type="date"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
                borderRadius="md"
              />
            </Box>

            {/* Gateway */}
            <Box minW="130px">
              <Text fontSize="11px" fontWeight="bold" color="gray.500" mb={1} textTransform="uppercase">
                Payment Gateway
              </Text>
              <Select
                size="sm"
                value={gatewayFilter}
                onChange={(e) => setGatewayFilter(e.target.value)}
                borderRadius="md"
              >
                <option value="all">All Gateways</option>
                <option value="stripe">Stripe</option>
                <option value="paypal">PayPal</option>
              </Select>
            </Box>

            {/* Order Status */}
            <Box minW="130px">
              <Text fontSize="11px" fontWeight="bold" color="gray.500" mb={1} textTransform="uppercase">
                Order Status
              </Text>
              <Select
                size="sm"
                value={orderStatusFilter}
                onChange={(e) => setOrderStatusFilter(e.target.value)}
                borderRadius="md"
              >
                <option value="all">All Statuses</option>
                <option value="paid">Paid</option>
                <option value="refunded">Refunded</option>
                <option value="partially_refunded">Partially Refunded</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </Box>

            {/* Booking Fee Toggle (Default: ON) */}
            <Box minW="170px">
              <Text fontSize="11px" fontWeight="bold" color="gray.500" mb={1} textTransform="uppercase">
                Booking Fee
              </Text>
              <Flex
                align="center"
                h="32px"
                px={3}
                borderRadius="md"
                border="1px solid"
                borderColor={borderColor}
                bg={selectBg}
                justify="space-between"
              >
                <Text fontSize="xs" fontWeight="600" color={includeBookingFee ? 'brand.500' : 'gray.500'}>
                  {includeBookingFee ? 'Included (ON)' : 'Excluded (OFF)'}
                </Text>
                <Switch
                  size="sm"
                  colorScheme="brand"
                  isChecked={includeBookingFee}
                  onChange={(e) => setIncludeBookingFee(e.target.checked)}
                />
              </Flex>
            </Box>
          </HStack>

          <Button
            size="sm"
            variant="ghost"
            colorScheme="gray"
            onClick={handleResetFilters}
            mt={{ base: 2, md: 4 }}
          >
            Reset Filters
          </Button>
        </Flex>
      </Card>

      {/* KPI Cards Grid */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3, '2xl': includeBookingFee ? 6 : 5 }} gap="20px" mb="25px">
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg="linear-gradient(90deg, #4481EB 0%, #04BEFE 100%)"
              icon={<Icon w="28px" h="28px" as={MdAttachMoney} color="white" />}
            />
          }
          name={includeBookingFee ? (selectedEventId !== 'all' ? "Gross Ticket Sales" : "Gross Revenue") : "Total Ticket Sales"}
          value={`€${Number(eventReportData?.grossSales ?? summary.total_revenue ?? 0).toFixed(2)}`}
        />

        {includeBookingFee && (
          <MiniStatistics
            startContent={
              <IconBox
                w="56px"
                h="56px"
                bg="linear-gradient(90deg, #01B574 0%, #51E198 100%)"
                icon={<Icon w="28px" h="28px" as={MdReceiptLong} color="white" />}
              />
            }
            name="Booking Fees Charged"
            value={`€${Number(eventReportData?.bookingFeesCharged ?? (summary.total_booking_fees || summary.total_tax || 0)).toFixed(2)}`}
          />
        )}

        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg="linear-gradient(90deg, #7551FF 0%, #B09FFF 100%)"
              icon={<Icon w="28px" h="28px" as={MdConfirmationNumber} color="white" />}
            />
          }
          name="Tickets Sold"
          value={selectedEventId !== 'all' ? (eventReportData?.soldTicketsCount ?? summary.total_tickets_sold ?? 0) : (summary.total_tickets_sold || 0)}
        />

        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg="linear-gradient(90deg, #FFB547 0%, #FFD680 100%)"
              icon={<Icon w="28px" h="28px" as={MdEvent} color="white" />}
            />
          }
          name={selectedEventId !== 'all' ? "Event Status" : "Events"}
          value={selectedEventId !== 'all' ? (eventReportData?.event?.status || 'FILTERED') : `${summary.published_events || 0} / ${summary.total_events || 0}`}
        />

        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg="linear-gradient(90deg, #EE5D50 0%, #FF8A80 100%)"
              icon={<Icon w="28px" h="28px" as={MdOutlineRemoveShoppingCart} color="white" />}
            />
          }
          name="Refunded Amount"
          value={`€${Number(eventReportData?.totalRefunds ?? summary.total_refunded_amount ?? 0).toFixed(2)}`}
        />

        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<Icon w="28px" h="28px" as={MdTrendingUp} color="brand.500" />}
            />
          }
          name={selectedEventId !== 'all' ? "Net Bank Settlement" : "Avg Order Value"}
          value={selectedEventId !== 'all' ? `€${Number(eventReportData?.netSales ?? (summary.total_revenue - (summary.total_refunded_amount || 0))).toFixed(2)}` : `€${Number(summary.avg_order_value || 0).toFixed(2)}`}
        />
      </SimpleGrid>

      {/* EVENT-SPECIFIC REPORTING SECTION (Audited Financial Settlement) */}
      {selectedEventId !== 'all' && eventReportData && (
        <VStack spacing="20px" align="stretch" mb="25px">
          {/* Event Executive Overview Banner */}
          <Card p="24px" borderLeft="4px solid" borderColor="brand.500">
            <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
              <Box>
                <HStack spacing={2} mb={1}>
                  <Badge colorScheme="brand" px={2} py={0.5} borderRadius="full">
                    Event Statement
                  </Badge>
                  <Badge colorScheme="green" px={2} py={0.5} borderRadius="full">
                    {eventReportData.event?.status}
                  </Badge>
                </HStack>
                <Text color={textColor} fontSize="xl" fontWeight="bold">
                  {eventReportData.event?.title}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  📅 {eventReportData.eventDate} &bull; 📍 {eventReportData.venueName} &bull; Currency: {eventReportData.currency} ({eventReportData.currencySymbol})
                </Text>
              </Box>

              <HStack spacing={6}>
                <Box textAlign="right">
                  <Text fontSize="xs" color="gray.400" textTransform="uppercase">Capacity Sold</Text>
                  <Text fontSize="lg" fontWeight="bold" color="brand.500">
                    {eventReportData.soldTicketsCount} / {eventReportData.totalCapacity} ({eventReportData.capacityPercent}%)
                  </Text>
                </Box>
                <Box textAlign="right">
                  <Text fontSize="xs" color="gray.400" textTransform="uppercase">Net Revenue</Text>
                  <Text fontSize="lg" fontWeight="bold" color="green.500">
                    {eventReportData.currencySymbol}{Number(eventReportData.netSales || 0).toFixed(2)}
                  </Text>
                </Box>
              </HStack>
            </Flex>
          </Card>

          {/* Orders & Admissions Breakdown */}
          <Card p="24px">
            <Flex justify="space-between" align="center" mb="16px" wrap="wrap" gap={2}>
              <Box>
                <HStack spacing={2}>
                  <Badge colorScheme="blue" px={2} py={0.5} borderRadius="md" fontSize="10px">
                    Settlement Statement
                  </Badge>
                  <Text color={textColor} fontSize="md" fontWeight="bold">
                    Orders & Admissions
                  </Text>
                </HStack>
                <Text color={textColorSecondary} fontSize="xs" mt={1}>
                  {includeBookingFee 
                    ? 'Aggregated products and booking fees by rate bracket' 
                    : 'Aggregated ticket products and admissions'}
                </Text>
              </Box>
            </Flex>

            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead bg={tableTheadBg}>
                  {includeBookingFee ? (
                    <Tr>
                      <Th borderColor={borderColor}>Event / Product</Th>
                      <Th borderColor={borderColor} textAlign="right">Price</Th>
                      <Th borderColor={borderColor} textAlign="right">Booking Fee Rate</Th>
                      <Th borderColor={borderColor} textAlign="right">Qty</Th>
                      <Th borderColor={borderColor} textAlign="right">Total (Net)</Th>
                      <Th borderColor={borderColor} textAlign="right">Booking Fee</Th>
                      <Th borderColor={borderColor} textAlign="right">Total (Gross)</Th>
                    </Tr>
                  ) : (
                    <Tr>
                      <Th borderColor={borderColor}>Event / Product</Th>
                      <Th borderColor={borderColor} textAlign="right">Price</Th>
                      <Th borderColor={borderColor} textAlign="right">Qty</Th>
                      <Th borderColor={borderColor} textAlign="right">Total</Th>
                    </Tr>
                  )}
                </Thead>
                <Tbody>
                  {eventReportData.settlement_orders?.lines?.map((line, idx) => (
                    includeBookingFee ? (
                      <Tr key={idx}>
                        <Td borderColor={borderColor} fontWeight={line.name.includes('Ticket') ? '600' : 'normal'} color={textColor}>
                          {line.name}
                        </Td>
                        <Td borderColor={borderColor} textAlign="right">
                          {formatEur(line.price)}
                        </Td>
                        <Td borderColor={borderColor} textAlign="right">
                          {Number(line.tax_rate).toFixed(1)}%
                        </Td>
                        <Td borderColor={borderColor} textAlign="right" fontWeight="600" color={line.count > 0 ? "brand.500" : "gray.400"}>
                          {line.count}
                        </Td>
                        <Td borderColor={borderColor} textAlign="right">
                          {formatEur(line.net)}
                        </Td>
                        <Td borderColor={borderColor} textAlign="right" color="amber.600">
                          {formatEur(line.tax)}
                        </Td>
                        <Td borderColor={borderColor} textAlign="right" fontWeight="600">
                          {formatEur(line.gross)}
                        </Td>
                      </Tr>
                    ) : (
                      <Tr key={idx}>
                        <Td borderColor={borderColor} fontWeight={line.name.includes('Ticket') ? '600' : 'normal'} color={textColor}>
                          {line.name}
                        </Td>
                        <Td borderColor={borderColor} textAlign="right">
                          {formatEur(line.price)}
                        </Td>
                        <Td borderColor={borderColor} textAlign="right" fontWeight="600" color={line.count > 0 ? "brand.500" : "gray.400"}>
                          {line.count}
                        </Td>
                        <Td borderColor={borderColor} textAlign="right" fontWeight="600">
                          {formatEur(line.gross)}
                        </Td>
                      </Tr>
                    )
                  ))}

                  {/* Tax Subtotals (only shown when booking fee is ON) */}
                  {includeBookingFee && eventReportData.settlement_orders?.tax_subtotals?.map((sub, sIdx) => (
                    <Tr key={`sub-${sIdx}`} bg={tableRowAltBg} fontWeight="600" fontSize="xs">
                      <Td borderColor={borderColor} color={textColor}>Subtotal</Td>
                      <Td borderColor={borderColor} textAlign="right">&mdash;</Td>
                      <Td borderColor={borderColor} textAlign="right">{Number(sub.rate).toFixed(1)}%</Td>
                      <Td borderColor={borderColor} textAlign="right">&mdash;</Td>
                      <Td borderColor={borderColor} textAlign="right">{formatEur(sub.net)}</Td>
                      <Td borderColor={borderColor} textAlign="right" color="amber.600">{formatEur(sub.tax)}</Td>
                      <Td borderColor={borderColor} textAlign="right">{formatEur(sub.gross)}</Td>
                    </Tr>
                  ))}

                  {/* Grand Total */}
                  {includeBookingFee ? (
                    <Tr bg="gray.100" _dark={{ bg: 'navy.700' }} fontWeight="bold">
                      <Td borderColor={borderColor} color={textColor}>Total</Td>
                      <Td borderColor={borderColor} textAlign="right">&mdash;</Td>
                      <Td borderColor={borderColor} textAlign="right">&mdash;</Td>
                      <Td borderColor={borderColor} textAlign="right">&mdash;</Td>
                      <Td borderColor={borderColor} textAlign="right">
                        {formatEur(eventReportData.settlement_orders?.total?.net || 0)}
                      </Td>
                      <Td borderColor={borderColor} textAlign="right" color="amber.600">
                        {formatEur(eventReportData.settlement_orders?.total?.tax || 0)}
                      </Td>
                      <Td borderColor={borderColor} textAlign="right" fontWeight="extrabold">
                        {formatEur(eventReportData.settlement_orders?.total?.gross || 0)}
                      </Td>
                    </Tr>
                  ) : (
                    <Tr bg="gray.100" _dark={{ bg: 'navy.700' }} fontWeight="bold">
                      <Td borderColor={borderColor} color={textColor}>Total</Td>
                      <Td borderColor={borderColor} textAlign="right">&mdash;</Td>
                      <Td borderColor={borderColor} textAlign="right" fontWeight="extrabold" color="brand.500">
                        {eventReportData.settlement_orders?.lines?.reduce((sum, l) => sum + (l.count || 0), 0) || 0}
                      </Td>
                      <Td borderColor={borderColor} textAlign="right" fontWeight="extrabold">
                        {formatEur(eventReportData.settlement_orders?.total?.gross || 0)}
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </Card>

          {/* Payments & Open Items Side-by-Side */}
          <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
            {/* Payments & Settlements */}
            <Card p="24px">
              <HStack spacing={2} mb={2}>
                <Badge colorScheme="purple" px={2} py={0.5} borderRadius="md" fontSize="10px">
                  Payment Processing
                </Badge>
                <Text color={textColor} fontSize="md" fontWeight="bold">
                  Payments & Settlements
                </Text>
              </HStack>
              <Text color={textColorSecondary} fontSize="xs" mb={4}>
                Verified settlements and refunds by payment processor (PayPal & Stripe separated)
              </Text>
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead bg={tableTheadBg}>
                    <Tr>
                      <Th borderColor={borderColor}>Payment Method</Th>
                      <Th borderColor={borderColor} textAlign="right">Payments Received</Th>
                      <Th borderColor={borderColor} textAlign="right">Refunds</Th>
                      <Th borderColor={borderColor} textAlign="right">Net Total</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {eventReportData.settlement_payments?.rows?.map((pRow, idx) => (
                      <Tr key={idx}>
                        <Td borderColor={borderColor} fontWeight="600" color={textColor}>
                          {pRow.name}
                        </Td>
                        <Td borderColor={borderColor} textAlign="right">
                          €{Number(pRow.payments).toFixed(2)}
                        </Td>
                        <Td borderColor={borderColor} textAlign="right" color="red.500">
                          {pRow.refunds > 0 ? `€${Number(pRow.refunds).toFixed(2)}` : '—'}
                        </Td>
                        <Td borderColor={borderColor} textAlign="right" fontWeight="600" color="green.600">
                          €{Number(pRow.total).toFixed(2)}
                        </Td>
                      </Tr>
                    ))}
                    <Tr fontWeight="bold" bg="gray.100" _dark={{ bg: 'navy.700' }}>
                      <Td borderColor={borderColor}>{eventReportData.settlement_payments?.total?.name || 'Total'}</Td>
                      <Td borderColor={borderColor} textAlign="right">
                        €{Number(eventReportData.settlement_payments?.total?.payments || 0).toFixed(2)}
                      </Td>
                      <Td borderColor={borderColor} textAlign="right" color="red.500">
                        €{Number(eventReportData.settlement_payments?.total?.refunds || 0).toFixed(2)}
                      </Td>
                      <Td borderColor={borderColor} textAlign="right" fontWeight="extrabold" color="green.600">
                        €{Number(eventReportData.settlement_payments?.total?.total || 0).toFixed(2)}
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Box>
            </Card>

            {/* Account Reconciliation & Open Items */}
            <Card p="24px">
              <HStack spacing={2} mb={2}>
                <Badge colorScheme="green" px={2} py={0.5} borderRadius="md" fontSize="10px">
                  Balanced Ledger
                </Badge>
                <Text color={textColor} fontSize="md" fontWeight="bold">
                  Account Reconciliation & Open Items
                </Text>
              </HStack>
              <Text color={textColorSecondary} fontSize="xs" mb={4}>
                End-of-event account balancing statement across orders and payouts
              </Text>
              <VStack spacing={3} align="stretch" divider={<Divider />}>
                <Flex justify="space-between" fontSize="sm">
                  <Text color="gray.500">Orders Gross Total:</Text>
                  <Text fontWeight="bold">
                    +€{Number(eventReportData.settlement_open_items?.orders || 0).toFixed(2)}
                  </Text>
                </Flex>
                <Flex justify="space-between" fontSize="sm">
                  <Text color="gray.500">Payments Received:</Text>
                  <Text fontWeight="bold" color="blue.500">
                    -€{Number(eventReportData.settlement_open_items?.payments || 0).toFixed(2)}
                  </Text>
                </Flex>
                <Flex justify="space-between" fontSize="sm">
                  <Text color="gray.500">Refunds Distributed:</Text>
                  <Text fontWeight="bold" color="red.500">
                    +€{Number(eventReportData.settlement_open_items?.refunds || 0).toFixed(2)}
                  </Text>
                </Flex>
                <Flex justify="space-between" fontSize="md" fontWeight="extrabold" pt={1}>
                  <Text color={textColor}>
                    Outstanding Balance as of {eventReportData.createdTimestampEnglish || eventReportData.createdTimestamp}:
                  </Text>
                  <Text color="green.500">
                    = €{Number(eventReportData.settlement_open_items?.balance || 0).toFixed(2)}
                  </Text>
                </Flex>
              </VStack>
            </Card>
          </SimpleGrid>
        </VStack>
      )}

      {/* Row 1: Sales Trend & Status Breakdown Charts */}
      {selectedEventId === 'all' && (
        <SimpleGrid columns={{ base: 1, lg: 3 }} gap="20px" mb="25px">
          <Card gridColumn={{ lg: 'span 2' }} p="24px">
            <Flex justify="space-between" align="center" mb="15px">
              <Box>
                <Text color={textColor} fontSize="lg" fontWeight="700">
                  Revenue & Sales Volume Over Time
                </Text>
                <Text color={textColorSecondary} fontSize="xs">
                  Daily ticket revenues and total transaction counts
                </Text>
              </Box>
            </Flex>
            <Box minH="300px">
              <ReactApexChart
                options={salesTrendOptions}
                series={charts.sales_trend?.series || []}
                type="area"
                height={300}
              />
            </Box>
          </Card>

          <Card p="24px">
            <Box mb="15px">
              <Text color={textColor} fontSize="lg" fontWeight="700">
                Orders Status Distribution
              </Text>
              <Text color={textColorSecondary} fontSize="xs">
                Breakdown of completed, pending, refunded, and cancelled transactions
              </Text>
            </Box>
            <Box minH="300px" display="flex" alignItems="center" justifyContent="center">
              <ReactApexChart
                options={statusDonutOptions}
                series={charts.status_breakdown?.series || []}
                type="donut"
                height={280}
              />
            </Box>
          </Card>
        </SimpleGrid>
      )}

      {/* Row 2: Revenue by Event & Detailed Orders Table */}
      <SimpleGrid columns={{ base: 1, lg: selectedEventId === 'all' ? 3 : 1 }} gap="20px" mb="25px">
        {selectedEventId === 'all' && (
          <Card p="24px">
            <Box mb="15px">
              <Text color={textColor} fontSize="lg" fontWeight="700">
                Revenue by Event
              </Text>
              <Text color={textColorSecondary} fontSize="xs">
                Top events generating ticket proceeds
              </Text>
            </Box>
            <Box minH="320px">
              <ReactApexChart
                options={eventBarOptions}
                series={charts.revenue_by_event?.series || []}
                type="bar"
                height={320}
              />
            </Box>
          </Card>
        )}

        <Card gridColumn={{ lg: selectedEventId === 'all' ? 'span 2' : 'span 1' }} p="24px" overflow="hidden" display="flex" flexDirection="column">
          <Flex justify="space-between" align="center" mb="12px" wrap="wrap" gap="10px">
            <Flex align="center" gap="10px">
              <Text color={textColor} fontSize="lg" fontWeight="700">
                Detailed Transaction Records
              </Text>
              <Badge colorScheme="brand" px="3" py="1" borderRadius="full">
                {filteredOrders.length} Orders {selectedEventId !== 'all' ? `(${totalTicketsInFilteredOrders} Tickets)` : ''}
              </Badge>
            </Flex>
            <Text color={textColorSecondary} fontSize="xs">
              {includeBookingFee 
                ? 'Audit order references, booking fees, gateways, and manage refunds' 
                : 'Audit order references, ticket payments, gateways, and manage refunds'}
            </Text>
          </Flex>

          {/* Orders Filters Bar */}
          <Flex gap="8px" mb="14px" wrap="wrap" align="center">
            <InputGroup size="sm" maxW={{ base: '100%', md: '240px' }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search order #, customer..."
                value={orderSearchQuery}
                onChange={(e) => setOrderSearchQuery(e.target.value)}
                borderRadius="md"
              />
            </InputGroup>

            {orderSearchQuery && (
              <Button
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => setOrderSearchQuery('')}
              >
                Clear Search
              </Button>
            )}
          </Flex>

          <Box maxH="440px" overflowY="auto" overflowX="auto" pr="4px">
            <Table variant="simple" color="gray.500" size="sm">
              <Thead position="sticky" top={0} bg={tableTheadBg} zIndex={1}>
                <Tr>
                  <Th borderColor={borderColor}>Order # / Tickets</Th>
                  {selectedEventId === 'all' && (
                    <Th borderColor={borderColor}>Customer</Th>
                  )}
                  <Th borderColor={borderColor}>Event</Th>
                  {includeBookingFee && (
                    <Th borderColor={borderColor} textAlign="right">Booking Fee</Th>
                  )}
                  <Th borderColor={borderColor} textAlign="right">Total</Th>
                  <Th borderColor={borderColor} textAlign="center">Gateway</Th>
                  <Th borderColor={borderColor} textAlign="center">Status</Th>
                  <Th borderColor={borderColor} textAlign="center">Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredOrders.length === 0 ? (
                  <Tr>
                    <Td colSpan={selectedEventId === 'all' ? (includeBookingFee ? 8 : 7) : (includeBookingFee ? 7 : 6)} textAlign="center" py="24px" color="gray.400">
                      No orders match the selected filters.
                    </Td>
                  </Tr>
                ) : (
                  filteredOrders.map((ord) => (
                    <Tr key={ord.id}>
                      <Td borderColor={borderColor} fontWeight="700" color={textColor}>
                        <Text>{ord.order_number}</Text>
                        <Badge colorScheme="purple" fontSize="9px" px={2} py={0.5} borderRadius="md" mt={1}>
                          {ord.tickets_count || 1} {ord.tickets_count === 1 ? 'Ticket' : 'Tickets'}
                        </Badge>
                      </Td>
                      {selectedEventId === 'all' && (
                        <Td borderColor={borderColor}>
                          <Text fontSize="xs" fontWeight="600" color={textColor}>
                            {ord.customer_name}
                          </Text>
                          <Text fontSize="10px" color="gray.400">
                            {ord.customer_email}
                          </Text>
                        </Td>
                      )}
                      <Td borderColor={borderColor} maxW="160px" isTruncated title={ord.event_title}>
                        <Text fontSize="xs" isTruncated>
                          {ord.event_title}
                        </Text>
                      </Td>
                      {includeBookingFee && (
                        <Td borderColor={borderColor} textAlign="right" color="amber.600" fontWeight="600">
                          €{Number(ord.booking_fee || ord.tax_amount || 0).toFixed(2)}
                        </Td>
                      )}
                      <Td borderColor={borderColor} textAlign="right" fontWeight="700" color={textColor}>
                        €{Number(includeBookingFee ? ord.total_amount : Math.max(0, (ord.total_amount || 0) - (ord.booking_fee || ord.tax_amount || 0))).toFixed(2)}
                      </Td>
                      <Td borderColor={borderColor} textAlign="center">
                        <Badge variant="subtle" fontSize="9px" px={2} py={0.5}>
                          {ord.payment_gateway || 'card'}
                        </Badge>
                      </Td>
                      <Td borderColor={borderColor} textAlign="center">
                        <Badge
                          colorScheme={
                            ord.payment_status === 'paid'
                              ? 'green'
                              : ['refunded', 'partially_refunded'].includes(ord.payment_status)
                              ? 'red'
                              : ord.payment_status === 'cancelled'
                              ? 'gray'
                              : 'orange'
                          }
                          borderRadius="8px"
                          px="8px"
                          py="2px"
                        >
                          {ord.payment_status}
                        </Badge>
                      </Td>
                      <Td borderColor={borderColor} textAlign="center">
                        {ord.payment_status === 'paid' ? (
                          <Button
                            size="xs"
                            colorScheme="red"
                            variant="outline"
                            borderRadius="8px"
                            onClick={() => handleOpenRefund(ord)}
                          >
                            Refund
                          </Button>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            -
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </Card>
      </SimpleGrid>

      {/* Refund Confirmation Modal */}
      <Modal isOpen={isRefundOpen} onClose={onCloseRefund} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Refund Order {selectedOrder?.order_number}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color={textColorSecondary} mb={4}>
              Are you sure you want to refund this order of{' '}
              <strong>€{Number(selectedOrder?.total_amount || 0).toFixed(2)}</strong>? The funds will
              be reversed back to the buyer's payment method and all associated seats will be released
              back to availability.
            </Text>
            <FormControl>
              <FormLabel fontSize="xs">Reason for Refund</FormLabel>
              <Textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="e.g. Customer requested cancellation before deadline"
                fontSize="sm"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCloseRefund}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              isLoading={refundMutation.isPending}
              onClick={handleConfirmRefund}
            >
              Confirm Refund
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
